import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import Moment from 'react-moment';
import classNames from 'classnames';
import { chatApi } from '@/api';
import { useCancelToken } from '@/hooks/useCancelToken';
import Preloader from '@UI/Preloader';
import { MessageView } from '@common/Chat/components/ChatMessage/ChatMessage';
import { convertURLs } from '@common/Chat/helpers/convertURLs';
import s from './MessageSearchPanel.module.css';
import { IconSearch } from '@/utils/icons';
import ChatTopCollapsePanel from '../ChatTopCollapsePanel/ChatTopCollapsePanel';

const SEARCH_MIN_LENGTH = 3;
const SEARCH_LIMIT = 1000;

const MessageSearchPanel = ({
  chatId,
  members = [],
  onSelectMessage,
  className = '',
  activePanel,
  setActivePanel,
  standalone = false,
}) => {
  const [searchValue, setSearchValue] = useState('');
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [errorText, setErrorText] = useState('');
  const searchRequestIdRef = useRef(0);
  const isMountedRef = useRef(true);
  const { getCancelTokenSource, cancel } = useCancelToken();

  const authorsByMemberId = useMemo(() => {
    return new Map(members.map((member) => [String(member.member_id), member]));
  }, [members]);

  const preparedResults = useMemo(
    () => results.map((message) => ({
      ...message,
      message: convertURLs(message?.message),
    })),
    [results],
  );

  const handleSearch = async () => {
    const preparedSearchValue = searchValue.trim();
    const requestId = searchRequestIdRef.current + 1;
    searchRequestIdRef.current = requestId;

    setHasSearched(true);

    if (preparedSearchValue.length < SEARCH_MIN_LENGTH) {
      cancel('Недостаточно символов для поиска');
      setErrorText(`Введите не менее ${SEARCH_MIN_LENGTH} символов`);
      setResults([]);
      return;
    }

    setErrorText('');
    setIsLoading(true);

    const cancelTokenSource = getCancelTokenSource();

    try {
      const response = await chatApi.searchMessages({
        chat_id: chatId,
        search: preparedSearchValue,
        limit: SEARCH_LIMIT,
        offset: 0,
      }, cancelTokenSource);

      if (isMountedRef.current && searchRequestIdRef.current === requestId) {
        setResults(response?.data?.data?.messages ?? []);
      }
    } catch (error) {
      if (!axios.isCancel(error) && isMountedRef.current && searchRequestIdRef.current === requestId) {
        setErrorText('Не удалось выполнить поиск');
        setResults([]);
      }
    } finally {
      if (isMountedRef.current && searchRequestIdRef.current === requestId) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    searchRequestIdRef.current += 1;
    cancel('Смена чата');
    setSearchValue('');
    setResults([]);
    setIsLoading(false);
    setHasSearched(false);
    setErrorText('');
  }, [chatId]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleSubmit = (event) => {
    event.preventDefault();
    handleSearch();
  };

  const handleSelectMessage = async (message) => {
    await onSelectMessage?.(message);
  };

  const content = (
    <>
      <form className={s.form} onSubmit={handleSubmit}>
        <input
          value={searchValue}
          onChange={(event) => setSearchValue(event.target.value)}
          className={s.input}
          placeholder="Введите текст сообщения"
          autoFocus={standalone}
        />
        <button type="submit" className={s.button}>
          <IconSearch />
        </button>
      </form>

      {!!errorText && (
        <div className={classNames(s.stateText, s.errorText)}>
          {errorText}
        </div>
      )}

      {isLoading && (
        <div className={s.preloaderWrapper}>
          <Preloader />
        </div>
      )}

      {!isLoading && hasSearched && !errorText && preparedResults.length === 0 && (
        <div className={s.stateText}>Ничего не найдено</div>
      )}

      {!isLoading && preparedResults.length > 0 && (
        <div className={classNames('new-design-scroll', s.results)}>
          {preparedResults.map((message) => {
            const author = authorsByMemberId.get(String(message.author));

            return (
              <button
                key={message.id}
                type="button"
                className={s.resultItem}
                onClick={() => handleSelectMessage(message)}>
                <div className={s.resultHead}>
                  <span className={s.resultAuthor}>
                    {author?.name || 'Неизвестный пользователь'}
                  </span>
                  <span className={s.resultDate}>
                    <Moment locale="ru" date={message.createdAt} format="D MMM YYYY k:mm" />
                  </span>
                </div>
                <div className={s.resultText}>
                  <MessageView message={message.message} />
                </div>
              </button>
            );
          })}
        </div>
      )}
    </>
  );

  if (standalone) {
    return (
      <div className={classNames(s.standalone, className)}>
        {content}
      </div>
    );
  }

  return (
    <ChatTopCollapsePanel
      className={className}
      header="Поиск по сообщениям"
      panelKey="messageSearch"
      activePanel={activePanel}
      setActivePanel={setActivePanel}>
      {content}
    </ChatTopCollapsePanel>
  );
};

export default MessageSearchPanel;
